<template>
  <SlideIn
    :open="openFilters"
    :content-padding="false"
    heading="Filters"
    @close="openFilters = false"
  >
    <template #content>
      <div class="filters">
        <SelectedFilters
          :selected-filters="filters"
          @clearFilter="handleClearFilter"
          @resetFilters="handleResetClick"
        />
        <AccordionGroup>
          <Accordion title="Sort">
            <ul>
              <li v-for="option in sortOptions" :key="option.value">
                <button
                  class="list-filter-button"
                  :class="{
                    'list-filter-button--active':
                      option.value === activeSort.value,
                  }"
                  @click="handleChange(option)"
                >
                  {{ option.label }}
                </button>
              </li>
            </ul>
          </Accordion>
          <Accordion
            v-for="filter in sortedAvailableFilters"
            :key="filter.id"
            :title="filter.label"
          >
            <component
              :is="getFilterName(filter.type)"
              :values="filter.values"
              :filters="filters"
              @change="handleFilterChange"
            />
          </Accordion>
        </AccordionGroup>
      </div>
    </template>
    <template #footer>
      <div class="button-group">
        <Button theme="secondary" width="full" @click="handleResetClick">
          Clear All
        </Button>
        <Button width="full" @click="applyFilter">See Results</Button>
      </div>
    </template>
  </SlideIn>
</template>

<script>
import camelCase from "lodash-es/camelCase";
import cloneDeep from "lodash-es/cloneDeep";
import PriceFilter from "@/components/collection/filters/priceFilter.vue";
import ListFilter from "@/components/collection/filters/listFilter.vue";
import SelectedFilters from "@/components/collection/SelectedFilters.vue";
import AccordionGroup from "@/components/AccordionGroup.vue";
import Accordion from "@/components/AccordionV2.vue";
import Button from "@/components/ButtonV3.vue";
import { mapState, mapActions, mapGetters } from "vuex";
import isEqual from "lodash-es/isEqual";
import SlideIn from "@/components/SlideIn.vue";

const filterTypes = {
  LIST: "list",
  PRICE_RANGE: "price",
};

export default {
  name: "Filters",
  components: {
    PriceFilter,
    ListFilter,
    AccordionGroup,
    Accordion,
    SlideIn,
    Button,
    SelectedFilters,
  },
  emits: ["filterChange", "sortChange"],
  data() {
    return {
      filters: [],
    };
  },
  computed: {
    ...mapState("collection", ["isFiltersOpen", "sortOptions"]),
    ...mapGetters("collection", ["availableFilters", "activeSort"]),
    ...mapState("collection", {
      selectedFilters: (state) => state.filters,
    }),
    openFilters: {
      get() {
        return this.isFiltersOpen;
      },
      set(value) {
        this.setIsFiltersOpen(value);
      },
    },
    sortedAvailableFilters() {
      const filters = [];
      const sizeOrder = ["XXS", "XS", "S", "M", "L", "XL", "XXL"];

      this.availableFilters.forEach((filter) => {
        if (filter.id === "filter.v.option.size") {
          const clonedFilter = cloneDeep(filter);
          clonedFilter.values.sort((a, b) => {
            return sizeOrder.indexOf(a.label) - sizeOrder.indexOf(b.label);
          });
          return filters.push(clonedFilter);
        }

        filters.push(filter);
      });

      const filtersWithMoreThanOneOption = filters.filter((filter) => {
        const excludesIds = ["filter.v.price"];
        const valuesGreaterThanOne = filter.values.length > 1;

        if (excludesIds.includes(filter.id)) {
          return true;
        }

        if (filter.id === "filter.v.availability") {
          return false;
        }

        if (valuesGreaterThanOne) {
          return true;
        }

        return false;
      });

      return filtersWithMoreThanOneOption;
    },
  },
  watch: {
    selectedFilters() {
      this.filters = this.selectedFilters;
    },
  },
  methods: {
    ...mapActions("collection", ["setIsFiltersOpen", "setFilters", "setSort"]),
    handleChange(option) {
      const value = option.value;
      const reverse = value.includes("_DESC");
      const strippedValue = value.replace("_ASC", "").replace("_DESC", "");

      this.setSort({ value: strippedValue, reverse });
    },
    getFilterName(type) {
      const filterName = camelCase(filterTypes[type]);
      return filterName + "Filter";
    },
    handleFilterChange(input) {
      const selectedFilters = cloneDeep(this.filters) || [];
      const filterKey = Object.keys(input)[0];

      const filterIsSelected = selectedFilters.some((selectedFilter) =>
        isEqual(selectedFilter[filterKey], input[filterKey])
      );

      if (filterIsSelected || filterKey === "price") {
        const index = this.filters.findIndex((filter) => {
          return Object.keys(filter)[0] === Object.keys(input)[0];
        });

        if (index === -1) {
          this.filters.push(input);
        } else if (filterKey === "price") {
          this.filters[index] = input;
        } else {
          this.filters.splice(index, 1);
        }

        const containsSizeValue = this.filters.some(
          (item) => item.variantOption?.name === "size"
        );

        if (!containsSizeValue) {
          console.log("Does not contain size value");

          const indexToRemove = this.filters.findIndex((item) =>
            Object.prototype.hasOwnProperty.call(item, "available")
          );

          if (indexToRemove !== -1) {
            this.filters.splice(indexToRemove, 1);
          }
        }
      } else {
        if (filterKey === "variantOption" && input[filterKey].name === "size") {
          const containsAvailability = this.filters.some((item) =>
            Object.prototype.hasOwnProperty.call(item, "available")
          );

          if (!containsAvailability) {
            this.filters.push({ available: true });
          }
        }

        this.filters.push(input);
      }
    },
    applyFilter() {
      this.setIsFiltersOpen(false);
      this.$emit("filterChange", this.filters);
    },
    handleClearFilter(item) {
      const selectedFilters = cloneDeep(this.filters) || [];
      const filterKey = Object.keys(item)[0];

      const filterIsSelected = selectedFilters?.some((selectedFilter) => {
        if (isEqual(selectedFilter[filterKey], item[filterKey])) {
          return true;
        }
        return false;
      });

      if (filterIsSelected || filterKey == "price") {
        const index = this.filters.findIndex((filter) => isEqual(filter, item));

        this.filters.splice(index, 1);
      }
    },
    handleResetClick() {
      this.filters = [];
      this.setIsFiltersOpen(false);
      this.$emit("filterChange", this.filters);
    },
    shouldShowFilterCount(filter) {
      const hiddenFilterCountIds = ["filter.v.availability", "filter.v.price"];
      if (hiddenFilterCountIds.includes(filter.id)) {
        return false;
      }
      return true;
    },
  },
};
</script>

<style lang="scss" scoped>
.button-group {
  display: flex;
  gap: 10px;
}

.filters {
  @extend .containerV2;
}

:deep() {
  .filter-accordion.accordion--open {
    transition-timing-function: ease-out;
    background-color: $color-grey-light;
    padding-bottom: 10px;
    margin-bottom: 20px;

    .accordion__header {
      transition-timing-function: ease-out;
      padding-top: 20px;
    }
  }
}

.filter-accordion {
  margin-left: -20px;
  margin-right: -20px;
  padding-left: 20px;
  padding-right: 20px;
  transition: all 0.3s;
  transition-timing-function: ease-in;

  :deep() {
    .accordion__header {
      transition: padding-top 0.3s;
      transition-timing-function: ease-in;
      align-items: center;
      font-family: $body-font-family;
      text-transform: uppercase;
      width: 100%;
      font-size: 9px;
      background: none;
      outline: none;
      border: none;
      padding: 8px 0;
      font-weight: 600;
    }
  }
}

.list-filter-button {
  @include utility-label(15px);

  width: calc(100% + #{$v-container-padding--mobile * 2}px);
  display: block;
  background: none;
  outline: none;
  border: none;
  text-align: left;
  padding: 10px #{$v-container-padding--mobile}px;
  margin: 0 -#{$v-container-padding--mobile}px;

  @include respond-above(md) {
    width: calc(100% + #{$v-container-padding--desktop * 2}px);
    margin: 0 -#{$v-container-padding--desktop}px;
    padding: 10px #{$v-container-padding--desktop}px;
  }

  &--active {
    background-color: $color-grey-light;
    border-left: 2px solid $color-black;
    padding-left: calc(#{$v-container-padding--mobile}px - 2px);

    @include respond-above(md) {
      padding-left: calc(#{$v-container-padding--desktop}px - 2px);
    }
  }
}
</style>
